import React from 'react';
import { BarChart2, PieChart } from 'lucide-react';

/**
 * Компонент визуализации показателей ликвидности
 */
const LiquidityVisualization = ({ results }) => {
  if (!results) return null;

  // Расчет процентов для гистограммы коэффициентов ликвидности
  const currentRatioPercent = Math.min(Math.round(results.currentRatio * 50), 100);
  const quickRatioPercent = Math.min(Math.round(results.quickRatio * 50), 100);
  const cashRatioPercent = Math.min(Math.round(results.cashRatio * 100), 100);

  // Определение классов цвета для полосок гистограммы
  const getCurrentRatioColor = () => {
    if (results.assessments.currentRatio.status === 'good') return 'bg-blue-500';
    if (results.assessments.currentRatio.status === 'warning') return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getQuickRatioColor = () => {
    if (results.assessments.quickRatio.status === 'good') return 'bg-purple-500';
    if (results.assessments.quickRatio.status === 'warning') return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getCashRatioColor = () => {
    if (results.assessments.cashRatio.status === 'good') return 'bg-emerald-500';
    if (results.assessments.cashRatio.status === 'warning') return 'bg-yellow-500';
    return 'bg-red-500';
  };

  // Структура активов для круговой диаграммы
  const assets = [
    { name: 'Денежные средства', value: results.balanceLiquidity.a1, color: 'bg-emerald-500' },
    { name: 'Дебиторская задолженность', value: results.balanceLiquidity.a2, color: 'bg-blue-500' },
    { name: 'Запасы и прочие оборотные активы', value: results.balanceLiquidity.a3, color: 'bg-purple-500' },
    { name: 'Внеоборотные активы', value: results.balanceLiquidity.a4, color: 'bg-gray-500' }
  ];

  // Расчет общей суммы активов
  const totalAssets = assets.reduce((sum, item) => sum + item.value, 0);

  // Структура обязательств для круговой диаграммы
  const liabilities = [
    { name: 'Кредиторская задолженность', value: results.balanceLiquidity.l1, color: 'bg-red-500' },
    { name: 'Краткосрочные кредиты и займы', value: results.balanceLiquidity.l2, color: 'bg-orange-500' },
    { name: 'Долгосрочные обязательства', value: results.balanceLiquidity.l3, color: 'bg-yellow-500' },
    { name: 'Собственный капитал', value: results.balanceLiquidity.l4, color: 'bg-green-500' }
  ];

  // Расчет общей суммы пассивов
  const totalLiabilities = liabilities.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mb-8">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <BarChart2 className="w-5 h-5 mr-2 text-indigo-500" />
        Визуализация показателей ликвидности
      </h3>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Гистограмма коэффициентов ликвидности */}
        <div className="space-y-6">
          <h4 className="text-base font-medium text-gray-800 dark:text-gray-200">Сравнение коэффициентов ликвидности с нормативами</h4>
          
          {/* Текущая ликвидность */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Текущая ликвидность</span>
              <span className="text-gray-600 dark:text-gray-400">
                {results.currentRatio.toFixed(2)} / {results.assessments.currentRatio.standard.toFixed(2)}
              </span>
            </div>
            <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className={`h-full ${getCurrentRatioColor()} rounded-full transition-all duration-500`} 
                style={{ width: `${currentRatioPercent}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>0</span>
              <span>1</span>
              <span>2</span>
              <span>3</span>
            </div>
          </div>
          
          {/* Быстрая ликвидность */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Быстрая ликвидность</span>
              <span className="text-gray-600 dark:text-gray-400">
                {results.quickRatio.toFixed(2)} / {results.assessments.quickRatio.standard.toFixed(2)}
              </span>
            </div>
            <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className={`h-full ${getQuickRatioColor()} rounded-full transition-all duration-500`} 
                style={{ width: `${quickRatioPercent}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>0</span>
              <span>0.5</span>
              <span>1</span>
              <span>2</span>
            </div>
          </div>
          
          {/* Абсолютная ликвидность */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Абсолютная ликвидность</span>
              <span className="text-gray-600 dark:text-gray-400">
                {results.cashRatio.toFixed(2)} / {results.assessments.cashRatio.standard.toFixed(2)}
              </span>
            </div>
            <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className={`h-full ${getCashRatioColor()} rounded-full transition-all duration-500`} 
                style={{ width: `${cashRatioPercent}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>0</span>
              <span>0.2</span>
              <span>0.5</span>
              <span>1</span>
            </div>
          </div>
        </div>

        {/* Структура активов и пассивов */}
        <div className="space-y-6">
          <h4 className="text-base font-medium text-gray-800 dark:text-gray-200">Структура активов и обязательств</h4>
          
          {/* Структура активов */}
          <div>
            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Активы</h5>
            <div className="h-6 w-full rounded-full overflow-hidden flex">
              {assets.map((asset, index) => (
                <div 
                  key={index}
                  className={`${asset.color} transition-all duration-500`}
                  style={{ width: `${(asset.value / totalAssets) * 100}%` }}
                  title={`${asset.name}: ${((asset.value / totalAssets) * 100).toFixed(1)}%`}
                ></div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              {assets.map((asset, index) => (
                <div key={index} className="flex items-center text-xs">
                  <div className={`h-3 w-3 rounded-sm ${asset.color} mr-2`}></div>
                  <span className="text-gray-700 dark:text-gray-300 mr-2">{asset.name}</span>
                  <span className="text-gray-500 dark:text-gray-400 ml-auto">
                    {((asset.value / totalAssets) * 100).toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Структура обязательств */}
          <div className="mt-6">
            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Обязательства и собственный капитал</h5>
            <div className="h-6 w-full rounded-full overflow-hidden flex">
              {liabilities.map((liability, index) => (
                <div 
                  key={index}
                  className={`${liability.color} transition-all duration-500`}
                  style={{ width: `${(liability.value / totalLiabilities) * 100}%` }}
                  title={`${liability.name}: ${((liability.value / totalLiabilities) * 100).toFixed(1)}%`}
                ></div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              {liabilities.map((liability, index) => (
                <div key={index} className="flex items-center text-xs">
                  <div className={`h-3 w-3 rounded-sm ${liability.color} mr-2`}></div>
                  <span className="text-gray-700 dark:text-gray-300 mr-2">{liability.name}</span>
                  <span className="text-gray-500 dark:text-gray-400 ml-auto">
                    {((liability.value / totalLiabilities) * 100).toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Пояснение */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-start">
          <PieChart className="h-5 w-5 text-indigo-500 mt-0.5 flex-shrink-0" />
          <div className="ml-3">
            <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">
              Интерпретация визуализации
            </h4>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Гистограмма показывает соотношение фактических коэффициентов ликвидности к нормативным значениям. Чем выше заполнение шкалы, тем лучше показатель. Круговые диаграммы отображают структуру активов и обязательств предприятия, что позволяет оценить соотношение ликвидных средств к общей структуре баланса.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiquidityVisualization;